import React, { useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useCredits } from "../../context/CreditsContext";
import { ClipFields } from "./ClipFields";
import { EMPTY_CLIP, clipCost } from "../../hooks/useVideoStudio";

const ASPECTS = [["16:9", "Wide", "w-8 h-[18px]"], ["9:16", "Tall", "w-[14px] h-6"], ["1:1", "Square", "w-5 h-5"]];

export const AspectPicker = ({ value, onChange }) => (
  <div className="flex gap-2">
    {ASPECTS.map(([a, label, box]) => (
      <button key={a} onClick={() => onChange(a)} data-testid={`new-video-aspect-${a.replace(":", "x")}`}
        className={`flex-1 h-16 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-colors ${value === a ? "border-emerald-500 bg-emerald-50/60" : "border-slate-200 bg-white hover:border-slate-300"}`}>
        <span className={`${box} rounded-[3px] border-2 ${value === a ? "border-emerald-600" : "border-slate-400"}`} />
        <span className="text-[11px] text-slate-600">{label} · {a}</span>
      </button>
    ))}
  </div>
);

export const PaidNote = () => {
  const { credits } = useCredits();
  return <p className="text-[11px] text-slate-500" data-testid="video-paid-note">Videos use bought credits only · you have <b className="text-slate-800">{credits?.paid ?? "…"}</b></p>;
};

export const NewVideoForm = ({ projectId, pricing, onCreated }) => {
  const [clip, setClip] = useState(EMPTY_CLIP);
  const [aspect, setAspect] = useState("16:9");
  const [busy, setBusy] = useState(false);
  const { refresh } = useCredits();
  const cost = clipCost(pricing, clip.quality, clip.duration);

  const create = async () => {
    if (clip.prompt.trim().length < 3) return toast.error("Describe your video in a few words");
    setBusy(true);
    try {
      const v = await api.createVideo({ project_id: projectId, aspect, prompt: clip.prompt.trim(), photo_id: clip.photo?.id || null, quality: clip.quality, duration: clip.duration });
      toast.success("Making your first clip — this takes a minute or two");
      refresh();
      onCreated(v);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll p-4 sm:p-8" data-testid="new-video-form">
      <div className="max-w-xl space-y-5">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900 flex items-center gap-2"><Clapperboard size={18} className="text-emerald-600" /> New video</h2>
          <p className="text-sm text-slate-500 mt-1">Describe a scene and AI films it. Add more clips, captions and music afterwards.</p>
        </div>
        <AspectPicker value={aspect} onChange={setAspect} />
        <ClipFields value={clip} setValue={setClip} pricing={pricing} idp="new-video" />
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button onClick={create} disabled={busy} data-testid="new-video-create-btn"
            className="h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-medium flex items-center gap-2 hover:bg-emerald-700 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />} Create video · {cost} credits
          </button>
          <PaidNote />
        </div>
      </div>
    </div>
  );
};

export default NewVideoForm;
